import React, { useState, useRef, useContext, useEffect, useMemo } from 'react'
import {
    BreachWallContentType,
    BreachWallStyle,
    mapSubscriptionPackagesToFlatList,
} from '../../helpers/BreachWallTypes'
import { RequiredAccessLevel } from '@west-australian-newspapers/publication-types'
import { TheWestSection } from '@news-mono/common'
import {
    PublicationType,
    CardEvents,
    CollectionEvent,
    LinkClickedEvent,
    PlayerEvent,
    BreachEvent,
    BreachScreenContext,
    BreachScreenType,
    click_origin_breach_screen,
    DataLayerEventName,
    useFeature,
    useSubscribeWithGoogle,
    ViewSubscriptionItem,
} from '@news-mono/web-common'
import { useTheme } from '@emotion/react'
import { useAuthActions } from '../../../../user-registration/authentication/AuthenticationProvider'
import { useSection, useSectionMetaInfo } from '../../../../__helpers'
import { createLinkClickEvent } from '../../createLinkClickHandler'
import H from 'history'
import {
    BottomTextWrap,
    ButtonWrap,
    CTAHeading,
    CTASubHeading,
    InfoText,
    InfoTextLink,
    PromoRibbon,
    PuzzlesBreachCTAWrap,
    PuzzlesBreachWrap,
    SubscribeButton,
    SubscribeWithGoogleButton,
} from './PuzzlesBreachwall.styled'
import { getCartPayload } from '../../../../tracking/gtm/gtm-event-payloads'
import {
    rrmePackageOfferDetails,
    useUpdateBreachWallContext,
} from '../../common'
import { toItemListId, DEFAULT_CART_QUANTITY } from '../../../../tracking'
import { PuzzlesListingCards } from './PuzzlesListingCards'
import { PuzzleListingDescription } from '../../../../puzzles/PuzzleListing/TheWest/PuzzleListing.styled'
import { Logger } from '@west-australian-newspapers/typescript-log'

const defaultCreative = 'TW_Article_Breach_WestBlue_Design_PhotoPackages'
const defaultType: BreachWallContentType = 'default-2026'
const defaultDeal = ''

const puzzlesBackgroundImage = require('./assets/puzzle-tw-min.png')

export interface PuzzlesBreachProps {
    suppliedDeal: string
    suppliedType: BreachWallContentType
    suppliedCreative?: string
    publication?: PublicationType
    requiredAccess?: RequiredAccessLevel
    location: H.Location
    log: Logger
    onEvent: (
        event:
            | BreachEvent
            | PlayerEvent
            | LinkClickedEvent
            | CardEvents
            | CollectionEvent,
    ) => void
    title?: string
    bannerText?: string
    message?: string
    renditionType?: string
    enabledSwgCampaign?: boolean
}

export const PuzzlesBreachWallWrapper = ({
    suppliedDeal,
    suppliedType,
    suppliedCreative,
    publication,
    requiredAccess,
    onEvent,
    title,
    bannerText,
    message,
    renditionType,
    enabledSwgCampaign,
    log,
}: PuzzlesBreachProps) => {
    const section = useSection<TheWestSection>()
    const theme = useTheme()
    const buttonRef = useRef<HTMLButtonElement>(null)
    const { onSubscribeClick, onLoginClick } = useAuthActions()

    const sectionMeta = useSectionMetaInfo()
    const masthead = 'The West Australian'
    const breachName = section === 'genwest' ? 'default' : section
    const subscribeWithGoogleEnabled = useFeature('subscribe-with-google')
    const googleRrmeEnabled = useFeature('google-rrme-full-override')
    const subscribeWithGoogleBreachInterfaceEnabled = useFeature(
        `swg-breach-${breachName}`,
    )
    const isBlackFridayPromo = useFeature(`black-friday`)

    const breachDeal = useMemo(
        () => suppliedDeal || defaultDeal,
        [suppliedDeal],
    )
    const breachType = useMemo(
        () => suppliedType || defaultType,
        [suppliedType],
    )
    const breachCreative = suppliedCreative || defaultCreative

    let subscriptionPackages = rrmePackageOfferDetails(
        isBlackFridayPromo
            ? 'black-friday'
            : breachType
            ? breachType
            : 'Puzzles',
        log,
    )

    // Enter in a fallback scenario for when the subscription offer API doesn't return any packages for the breach type. This ensures the breach wall still attempts to renders with at least a default package, rather than breaking entirely.
    if (subscriptionPackages.length === 0) {
        log.warn(
            `No subscription packages found for breach type: ${breachType}. Check if the subscription offer API is returning the expected data.`,
        )
        subscriptionPackages = rrmePackageOfferDetails('default-2026', log)
    }

    // Puzzles is now one package type only
    const subPackage = subscriptionPackages[0]
    const pricePackage =
        subPackage.type == 'single-subscription'
            ? subPackage
            : subPackage.prices[0]
    const { ribbonTitle } = subscriptionPackages[0]
    const {
        packagePath,
        promoPrice,
        weeklyPrice,
        discount,
        googleSKU,
        offerCode,
    } = pricePackage

    const showSwG =
        (subscribeWithGoogleEnabled || googleRrmeEnabled) &&
        subscribeWithGoogleBreachInterfaceEnabled &&
        googleSKU

    // fire breach wall context update
    useUpdateBreachWallContext({
        publication: publication,
        requiredAccessLevel: requiredAccess || 'subscriber',
        breachDeal: suppliedDeal,
        breachCreativeName: breachCreative,
    })

    const breachScreenContext = useContext(BreachScreenContext)
    const breachContext = breachScreenContext.context
    const breachScreenType: BreachScreenType = 'Premium - Inline'

    const googleRrmeEnabledOverride = useFeature('google-rrme-full-override')

    // handle whenever 'subscription options' is clicked.
    const onAllPackagesClicked = React.useCallback(() => {
        createLinkClickEvent(breachCreative, breachDeal, onEvent)()
        onSubscribeClick({
            callToAction: click_origin_breach_screen,
            breachScreenType: 'Premium - Inline',
        })
    }, [breachCreative, breachDeal, onEvent, onSubscribeClick])

    const addToCartPayload = getCartPayload(
        { price: pricePackage, package: subPackage },
        masthead,
    )

    useSubscribeWithGoogle(
        buttonRef.current as HTMLButtonElement,
        googleSKU || 'everyday_digital_4weeks',
        sectionMeta,
        breachContext,
        undefined,
        googleRrmeEnabledOverride,
    )

    // when everything is loaded, fire off a view_item_list GA4 event
    useEffect(() => {
        const availableDeal = breachDeal === '' ? 'default' : breachDeal
        const breachAvailablePayload: BreachEvent['payload'] = {
            paywallType: 'freemium',
            leadDepth: 0,
            creative: breachCreative,
            deal: availableDeal,
            breachScreenType: 'Premium - Inline',
            popupMethod: 'auto',
            pricepoint: [7, 8],
        }
        onEvent({
            type: DataLayerEventName.breachScreenAvailable,
            originator: 'breachscreen',
            payload: breachAvailablePayload,
        })

        const flatSubscriptionList =
            mapSubscriptionPackagesToFlatList(subscriptionPackages)
        const viewSubscriptionItems: ViewSubscriptionItem[] =
            flatSubscriptionList.map((pkg, index) => ({
                index,
                deal_name: breachType,
                item_name: pkg.heading,
                item_id: pkg.rateCode,
                item_brand: masthead,
                component_path: pkg.packagePath,
                item_list_category: 'freemium',
                item_list_category2: 'Premium - Inline',
                item_list_name: breachCreative,
                item_list_id: toItemListId(breachCreative),
                price: pkg.weeklyPrice,
                promo_price: pkg.promoPrice || 0,
                discount: pkg.discount || 0,
                coupon: pkg.offerCode || '',
                quantity: DEFAULT_CART_QUANTITY,
                currency: 'AUD',
            })) || []
        onEvent({
            type: DataLayerEventName.viewSubscriptionItemListEvent,
            parentType: DataLayerEventName.viewItemListEvent,
            originator: 'breachscreen',
            payload: { items: viewSubscriptionItems },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <>
            <PuzzlesBreachWrap theme={theme}>
                {renditionType !== 'app' && bannerText && (
                    <PuzzleListingDescription>
                        {bannerText}
                    </PuzzleListingDescription>
                )}
                <PuzzlesBreachCTAWrap
                    bgImage={puzzlesBackgroundImage}
                    noBanner={!bannerText}
                    enabledSwgCampaign={enabledSwgCampaign}
                >
                    <CTAHeading>{title}</CTAHeading>
                    <CTASubHeading>{message}</CTASubHeading>
                    <ButtonWrap enabledSwgCampaign={enabledSwgCampaign}>
                        {!enabledSwgCampaign && (
                            <SubscribeButton
                                buttonWidth="wide"
                                type="button"
                                title="Subscribe Button"
                                aria-label="Subscribe button"
                                onClick={() => {
                                    createLinkClickEvent(
                                        'puzzles',
                                        breachDeal,
                                        onEvent,
                                        breachScreenType,
                                        1,
                                        addToCartPayload,
                                    )()
                                    onSubscribeClick({
                                        packagePath: packagePath,
                                        offerCode: offerCode,
                                        callToAction:
                                            click_origin_breach_screen,
                                        breachScreenType: breachScreenType,
                                    })
                                }}
                            >
                                Subscribe
                            </SubscribeButton>
                        )}

                        {showSwG && (
                            <SubscribeWithGoogleButton
                                ref={buttonRef}
                                type="button"
                                aria-label="Subscribe with google button"
                                onClick={() =>
                                    onEvent({
                                        type: DataLayerEventName.addToCart,
                                        originator: 'breachscreen',
                                        payload: {
                                            currency: 'AUD',
                                            value:
                                                (promoPrice
                                                    ? promoPrice
                                                    : weeklyPrice) *
                                                DEFAULT_CART_QUANTITY,
                                            items: [
                                                {
                                                    item_id: 'WST_DIGI_DD',
                                                    item_brand: masthead,
                                                    item_name:
                                                        'Everyday Digital',
                                                    price: weeklyPrice,
                                                    promo_price:
                                                        promoPrice || 0,
                                                    quantity:
                                                        DEFAULT_CART_QUANTITY,
                                                    discount: discount || 0,
                                                    coupon: offerCode || '',
                                                    item_variant: 'swg',
                                                },
                                            ],
                                        },
                                    })
                                }
                            />
                        )}

                        {ribbonTitle && (
                            <PromoRibbon>{ribbonTitle}</PromoRibbon>
                        )}
                    </ButtonWrap>
                    <BottomTextWrap>
                        <InfoText>
                            Looking for a different subscription?{' '}
                            <InfoTextLink
                                href={'#'}
                                onEvent={onAllPackagesClicked}
                            >
                                Browse all
                            </InfoTextLink>
                        </InfoText>
                        <InfoText>
                            Already have an account?{' '}
                            <InfoTextLink
                                href={'#'}
                                onEvent={() => onLoginClick('puzzles-cta')}
                            >
                                Log in here
                            </InfoTextLink>
                        </InfoText>
                    </BottomTextWrap>
                </PuzzlesBreachCTAWrap>

                <PuzzlesListingCards disabled={true} />
            </PuzzlesBreachWrap>
        </>
    )
}
